import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import { Dataset, DimensionGroup } from '../../types';
import { projectTo1D, create1DSummary } from '../../utils/visualization';

interface SummaryViewProps {
  dataset: Dataset;
  dimensionGroups: DimensionGroup[];
  selectedGroups?: string[];
  onSelect?: (groups: string[]) => void;
  onDrillDown?: (group: string) => void;
  width?: number;
  height?: number;
}

const SummaryView: React.FC<SummaryViewProps> = ({
  dataset,
  dimensionGroups,
  selectedGroups = [],
  onSelect,
  onDrillDown,
  width = 800,
  height = 200,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [tooltipData, setTooltipData] = useState<{ x: number; y: number; content: string } | null>(null);

  useEffect(() => {
    if (!svgRef.current || !dataset || !dimensionGroups.length) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Calculate dimensions
    const margin = { top: 40, right: 20, bottom: 40, left: 40 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    // Create the SVG group with margins
    const g = svg
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Calculate width for each group's chart
    const chartWidth = Math.min(200, Math.max(100, innerWidth / dimensionGroups.length - 20));

    // Create a title
    g.append('text')
      .attr('class', 'summary-title')
      .attr('x', 0)
      .attr('y', -20)
      .text('Dimension Groups (1D Projections)');

    // Create a container for each dimension group
    const groupContainers = g
      .selectAll('.group-container')
      .data(dimensionGroups)
      .enter()
      .append('g')
      .attr('class', 'group-container')
      .attr('transform', (_, i) => `translate(${i * (chartWidth + 20)}, 0)`);

    // Process each dimension group
    groupContainers.each(function (group, i, nodes) {
      const container = d3.select(nodes[i]);

      // Calculate 1D projection
      const projectionVector = group.projectionVector || Array(group.dimensions.length).fill(1); // Default to uniform weight

      const projectedValues = projectTo1D(dataset, group.dimensions, projectionVector);
      const { bins } = create1DSummary(dataset, projectedValues, 10);

      // Create scales
      const xScale = d3.scaleLinear().domain([0, bins.length]).range([0, chartWidth]);

      const maxBinCount = d3.max(bins, (d) => d.length) || 0;
      const yScale = d3.scaleLinear().domain([0, maxBinCount]).range([innerHeight, 0]);

      // Draw bars
      container
        .selectAll('.summary-bar')
        .data(bins)
        .enter()
        .append('rect')
        .attr('class', 'summary-bar')
        .classed('selected', () => selectedGroups.includes(group.id))
        .attr('x', (_, i) => xScale(i))
        .attr('y', (d) => yScale(d.length))
        .attr('width', chartWidth / bins.length - 1)
        .attr('height', (d) => innerHeight - yScale(d.length))
        .on('click', () => {
          const newSelection = selectedGroups.includes(group.id)
            ? selectedGroups.filter((id) => id !== group.id)
            : [...selectedGroups, group.id];

          if (onSelect) {
            onSelect(newSelection);
          }
        })
        .on('dblclick', () => {
          if (onDrillDown) {
            onDrillDown(group.id);
          }
        })
        .on('mouseover', (event, d) => {
          // Show tooltip
          const binIndex = bins.indexOf(d);
          const x = i * (chartWidth + 20) + xScale(binIndex) + chartWidth / bins.length / 2;
          const y = yScale(d.length) - 10;

          setTooltipData({
            x: x + margin.left,
            y: y + margin.top,
            content: `Count: ${d.length}<br>Range: ${d.x0?.toFixed(2)} - ${d.x1?.toFixed(2)}`,
          });
        })
        .on('mouseout', () => {
          setTooltipData(null);
        });

      // Add x-axis
      container
        .append('g')
        .attr('class', 'summary-axis')
        .attr('transform', `translate(0,${innerHeight})`)
        .call(d3.axisBottom(xScale).ticks(3));

      // Add group label
      container
        .append('text')
        .attr('class', 'summary-group-label')
        .attr('x', chartWidth / 2)
        .attr('y', innerHeight + 35)
        .attr('text-anchor', 'middle')
        .text(group.name);

      // Add dimension count badge
      container
        .append('text')
        .attr('class', 'summary-dimensions-count')
        .attr('x', chartWidth - 5)
        .attr('y', -5)
        .attr('text-anchor', 'end')
        .text(`${group.dimensions.length} dim${group.dimensions.length > 1 ? 's' : ''}`);
    });
  }, [dataset, dimensionGroups, selectedGroups, width, height, onSelect, onDrillDown]);

  return (
    <div className="summary-view" style={{ position: 'relative' }}>
      <svg ref={svgRef}></svg>

      {tooltipData && (
        <div
          className="viz-tooltip"
          style={{
            position: 'absolute',
            left: `${tooltipData.x}px`,
            top: `${tooltipData.y}px`,
            transform: 'translate(-50%, -100%)',
          }}
          dangerouslySetInnerHTML={{ __html: tooltipData.content }}
        />
      )}
    </div>
  );
};

export default SummaryView;
